import { useEffect, useRef, useState } from 'react'
import { t } from '@lingui/core/macro'
import { AnimatePresence, motion } from 'framer-motion'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useLocation } from 'react-use'
import { CopyableLabel } from '@components/v2/CopyableLabel'
import { Button } from '@components/v2/button'
import { Loader } from '@components/v2/loader'
import { OtpInput } from '@components/v2/otp-input'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { useEnableMfa } from './hooks/useEnableMfa'
import { useQueryMfaChallenge } from './hooks/useQueryMfaChallenge'

export const Setup = () => {
  const location = useLocation()
  const history = useHistory()
  const queryParams = new URLSearchParams(location.search)
  const email = queryParams.get('email') || ''
  const token = queryParams.get('mfa_token') || ''
  const [mfaCode, setMFACode] = useState('')
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    data,
    isLoading: isMfaChallengeLoading,
    isFetched: isMfaChallengeFetched,
  } = useQueryMfaChallenge({
    email,
    token,
  })

  useEffect(() => {
    inputRef.current?.focus()
  }, [inputRef?.current])

  const { qr_code, secret } = data || {}

  const {
    handleSubmit,
    error,
    isLoading: isEnableMfaLoading,
  } = useEnableMfa({
    email,
    token,
    code: mfaCode,
    secret,
    onSuccess: () => {
      // We need to remove the query params related to MFA (e.g. email and token)
      // but we need to keep the login=system query param (if present).

      const searchParams = new URLSearchParams(location.search)
      const loginParam = searchParams.get('login') === 'system' ? 'login=system' : ''
      const newSearch = loginParam ? `?${loginParam}` : ''

      history.replace(`/auth/login${newSearch}`)
    },
  })

  if (isMfaChallengeFetched && !qr_code && !secret) {
    history.replace(`/auth/login${location.search}`)
  }

  if (error) {
    dispatch(SnackbarActions.showSnackbar({ type: 'ERROR', error }))
  }

  const isLoading = isMfaChallengeLoading || isEnableMfaLoading

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <AnimatePresence initial={false}>
        {isLoading ? (
          <div className="w-full flex justify-center">
            <Loader className="w-7 h-7" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-cv-blue-600">
            <h2 className="font-bold text-[14px] mb-2">{t`Set up multi-factor authentication`}</h2>
            <div className="flex flex-col gap-2">
              <h3 className="text-[12px]">{t`Use QR code`}</h3>
              <div>
                {qr_code?.length > 0 && <img className="max-w-[120px]" src={`${qr_code}`} />}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-[12px]">{t`Or enter token manually`}</h3>
              <div>
                <CopyableLabel>
                  <CopyableLabel.Text>
                    <span className="text-[10px]">{secret}</span>
                  </CopyableLabel.Text>
                  <CopyableLabel.CopyTextButton text={secret} />
                </CopyableLabel>
              </div>
            </div>
            <form onSubmit={event => void handleSubmit(event)} className="flex flex-col gap-8">
              <div className="relative flex flex-col gap-2">
                <h3 className="text-[12px]">{t`Code from your authenticatior app`}</h3>
                <OtpInput
                  onChange={setMFACode}
                  inputRef={inputRef}
                  onComplete={() => {
                    setIsSubmitDisabled(false)
                  }}
                  value={mfaCode}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    history.replace(`/auth/login${location.search}`)
                  }}
                  variant="secondary"
                >
                  {t`Back`}
                </Button>
                <Button disabled={isSubmitDisabled} type="submit" variant="dark">
                  {t`Submit`}
                </Button>
              </div>
            </form>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
